'use client'
import type { ChangeEvent } from 'react'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'

import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'

import { ChevronDown, MenuIcon } from './blocks/Icons'
import type {
  AnnualReportPage,
  AnnualReportSection,
  Maybe,
} from '@/.generated/sanityTypes/graphql'
import Menu from './Menu'
import TbfPartner from '@/components/svg/TbfPartner'
import type { Route } from 'next'
import { usePathname } from 'next/navigation'
import { BsArrowLeft } from 'react-icons/bs'

type MenuHeaderProps = {
  setMenuOpen: (newMenuOpen: boolean) => void
  years: string[]
  year: string
}

const MenuHeader = ({ setMenuOpen, years, year }: MenuHeaderProps) => {
  const t = useTranslations('menu')
  const locale = useLocale()

  return (
    <div className="flex w-full transform items-center transition duration-500 ease-in-out">
      <button
        title={t('open')}
        className="ml-2 inline-block h-12 w-12 cursor-pointer self-center outline-none focus:outline-none"
        data-cy="open-menu-button"
        onClick={() => setMenuOpen(true)}
      >
        <MenuIcon color="rgb(218, 41, 28)" />
      </button>
      <div className="flex-1" style={{ marginRight: 56 }}>
        {/* TODO: This link only works locally */}
        <Link href={`/${locale}/${year}` as Route} title="TBF + Partner">
          <div className="mx-auto h-12 w-12 overflow-hidden object-cover object-left pt-1 sm:w-40 md:object-none">
            <TbfPartner className="mx-auto h-12 w-40" />
          </div>
        </Link>
      </div>
      <div className="absolute bottom-0 right-0 top-0 flex items-center space-x-2 pr-6">
        <YearSelector years={years} year={year} />
        <LanguageSelector year={year} />
      </div>
    </div>
  )
}

const YearSelector = ({ years, year }: { years: string[]; year: string }) => {
  const router = useRouter()
  const locale = useLocale()
  const t = useTranslations('header')

  const selectProps = {
    className:
      'relative py-1 pl-2 pr-4 text-red bg-transparent hover:bg-red/12 focus:bg-red/12 outline-none appearance-none cursor-pointer bg-none border-none text-xs',
    onChange: (e: ChangeEvent<HTMLSelectElement>) => {
      const { value: year } = e.target
      router.push(`/${locale}/${year}` as Route)
    },
    value: year,
  }

  const allOptions = [...years].sort().reverse()

  return (
    <div className="border-red relative overflow-hidden border text-xs font-[100]">
      <label className="sr-only md:hidden" htmlFor="yearMobile">
        {t('selectYear')}
      </label>
      <select
        id="yearMobile"
        {...selectProps}
        className={`${selectProps.className} md:hidden`}
      >
        {allOptions.map((yearOption) => (
          <option key={yearOption} value={yearOption}>
            &apos;{yearOption.slice(2, 4)}
          </option>
        ))}
      </select>
      <label className="sr-only hidden md:block" htmlFor="year">
        {t('selectYear')}
      </label>
      <select
        id="year"
        {...selectProps}
        className={`${selectProps.className} hidden md:block`}
      >
        {allOptions.map((yearOption) => (
          <option key={yearOption} value={yearOption}>
            {yearOption}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
        <ChevronDown className="fill-red w-2" />
      </div>
    </div>
  )
}

const LanguageSelector = ({ year }: { year: string }) => {
  const router = useRouter()
  const locale = useLocale()
  const t = useTranslations('header')

  const selectProps = {
    className:
      'relative py-1 pl-2 pr-4 text-red bg-transparent hover:bg-red/12 focus:bg-red/12 outline-none appearance-none cursor-pointer bg-none border-none text-xs',
    onChange: (e: ChangeEvent<HTMLSelectElement>) => {
      const { value: locale } = e.target
      router.push(`/${locale}/${year}` as Route)
    },
    value: locale,
  }
  return (
    <div className="border-red relative overflow-hidden border text-xs font-[100]">
      <label className="sr-only md:hidden" htmlFor="languagesMobile">
        {t('selectLanguage')}
      </label>
      <select
        id="languagesMobile"
        {...selectProps}
        className={`${selectProps.className} md:hidden`}
      >
        <option value="de">DE</option>
        <option value="fr">FR</option>
        <option value="it">IT</option>
        <option value="en">EN</option>
      </select>
      <label className="sr-only hidden md:block" htmlFor="languages">
        {t('selectLanguage')}
      </label>
      <select
        id="languages"
        {...selectProps}
        className={`${selectProps.className} hidden md:block`}
      >
        <option value="de">Deutsch</option>
        <option value="fr">Français</option>
        <option value="it">Italiano</option>
        <option value="en">English</option>
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2">
        <ChevronDown className="fill-red w-2" />
      </div>
    </div>
  )
}

// MAIN COMPONENT

const Header = ({
  sections,
  years,
  year,
  pages,
}: {
  sections?: Maybe<AnnualReportSection>[] | null
  pages?: AnnualReportPage[]
  years: string[]
  year: string
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const pathname = usePathname()
  const t = useTranslations('menu')
  const locale = useLocale()

  const menuPortal =
    typeof document !== `undefined`
      ? ReactDOM.createPortal(
          <Menu
            sections={sections}
            pages={pages}
            open={menuOpen}
            year={year}
            onClose={() => setMenuOpen(false)}
          />,
          document.body,
        )
      : null

  useEffect(() => {
    if (menuOpen) document.body.classList.add('overflow-hidden')
    else document.body.classList.remove('overflow-hidden')

    return () => document.body.classList.remove('overflow-hidden')
  }, [menuOpen])

  return (
    <>
      {/* TODO: investigate if we need this HEADROOM thingy */}
      {/* <HeadRoom style={{ zIndex: 40 }}> */}
      <div className="sticky top-0 z-40" style={{ height: '65px' }}>
        <div className="flex justify-center bg-white pb-3 pt-3 shadow">
          <MenuHeader years={years} setMenuOpen={setMenuOpen} year={year} />
        </div>
        {year === '2024' && pathname && pathname.split('/').length > 3 && (
          <div className="flex justify-end">
            <Link
              href={`/${locale}/${year}` as Route}
              className="bg-red text-md m-6 flex items-center gap-4 p-4 text-white"
            >
              <BsArrowLeft />
              <span className="hidden md:block">{t('backToOverview')}</span>
            </Link>
          </div>
        )}
      </div>
      {/* </HeadRoom> */}
      {menuPortal}
    </>
  )
}

export default Header
